import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAnnee } from '../hooks/useAnnee';
import { useAnomalies } from '../hooks/useAnomalies';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { Badge, Champ, Chargement, EnTetePage, Erreur, Modale } from '../components/Elements';
import type { Anomalie, PersonneEnAnomalie } from '../types';

const LIBELLES: Record<string, { titre: string; explication: string }> = {
  personne_sans_date_naissance: {
    titre: 'Adhérents sans date de naissance',
    explication:
      'Sans elle, ni la tranche d’âge ni les ventilations du tableau de bord ne sont calculables.',
  },
  personne_injoignable: {
    titre: 'Adhérents injoignables',
    explication:
      'Ni coordonnées propres, ni contact rattaché. Aucun envoi ciblé ne pourra les atteindre.',
  },
  personne_sans_referent: {
    titre: 'Adhérents sans contact référent',
    explication: 'Le référent est le destinataire par défaut des courriers et des factures.',
  },
  personne_sans_adresse: {
    titre: 'Adhérents sans adresse',
    explication: 'Aucun document postal ne pourra leur être adressé.',
  },
  personne_sans_droit_image: {
    titre: 'Droit à l’image non renseigné',
    explication:
      'Adhérents inscrits cette année dont l’autorisation signée n’est pas enregistrée : ni photo ni vidéo tant qu’elle n’est pas rentrée. Se saisit sur la fiche.',
  },
};

/**
 * Anomalies de donnees (RG-24).
 *
 * Chaque type s'ouvre sur la LISTE des adherents concernes : un compteur
 * qu'on ne peut pas ouvrir ne se corrige pas.
 *
 * Certaines anomalies sont ASSUMEES — un adherent qui refuse de donner sa
 * date de naissance restera sans date. Les rappeler chaque semaine finit
 * par faire ignorer la liste entiere, et c'est ainsi qu'une vraie anomalie
 * passe inapercue. L'exception vaut donc pour UNE ANNEE : l'annee
 * suivante, le cas revient a l'examen.
 */
export function AnomaliesPage() {
  const { peut, entiteActive } = useAuth();
  const cle = entiteActive?.id ?? '';
  const [ouverts, setOuverts] = useState<Set<string>>(new Set());
  const [aConfirmer, setAConfirmer] = useState<{
    type: string;
    personne: PersonneEnAnomalie;
  } | null>(null);

  // Les exceptions sont rattachees a une annee : l'ecran suit le bandeau.
  const annee = useAnnee();
  const compteur = useAnomalies();

  const anomalies = useRessource(
    () => api.get<Anomalie[]>('/api/anomalies', { anneeId: annee.anneeId ?? undefined }),
    [cle, annee.anneeId],
  );

  if (annee.chargement || anomalies.chargement) return <Chargement />;

  const liste = anomalies.donnees ?? [];
  const restant = liste.reduce((somme, a) => somme + a.nombre, 0);
  const assumees = liste.reduce(
    (somme, a) => somme + a.personnes.filter((p) => p.exception_id !== null).length,
    0,
  );
  const gerable = peut('anomalie.gerer') && annee.annee !== null;

  const basculer = (type: string) => {
    const suivante = new Set(ouverts);
    if (suivante.has(type)) suivante.delete(type);
    else suivante.add(type);
    setOuverts(suivante);
  };

  const lever = async (personne: PersonneEnAnomalie) => {
    if (!personne.exception_id) return;
    await api.delete(`/api/anomalies/exceptions/${personne.exception_id}`);
    anomalies.recharger();
    // La pastille du menu compte la meme chose : elle doit bouger en
    // meme temps que la liste, sans attendre un changement d'ecran.
    compteur.rafraichir();
  };

  return (
    <>
      <EnTetePage
        titre="Anomalies"
        description="Ce qui empêchera un calcul ou un envoi. Une anomalie assumée se coche : elle disparaît du compte, pour l’année en cours seulement."
      />

      {anomalies.erreur && <Erreur message={anomalies.erreur} onReessayer={anomalies.recharger} />}

      <div className="mb-4 flex flex-wrap items-center gap-4 text-sm text-encre-500">
        <span>
          <span className="tabulaire font-medium text-encre-900">{restant}</span> à traiter
        </span>
        {assumees > 0 && (
          <span>
            <span className="tabulaire font-medium text-encre-900">{assumees}</span> assumée(s) sur{' '}
            {annee.annee?.libelle}
          </span>
        )}
        {!annee.annee && (
          <span className="text-amber-700">
            Aucune année active : les exceptions ne peuvent pas être posées.
          </span>
        )}
      </div>

      <div className="space-y-3">
        {liste.map((anomalie) => {
          const details = LIBELLES[anomalie.type] ?? { titre: anomalie.type, explication: '' };
          const ouvert = ouverts.has(anomalie.type);
          const total = anomalie.personnes.length;

          return (
            <div
              key={anomalie.type}
              className={`carte ${anomalie.nombre > 0 ? 'border-amber-200' : ''}`}
            >
              {/* En-tete cliquable : c'est l'accordeon. Il reste ouvrable a
                  zero, pour qu'on puisse verifier qui a ete assume. */}
              <button
                type="button"
                className="flex w-full items-start gap-4 p-4 text-left hover:bg-encre-50"
                onClick={() => basculer(anomalie.type)}
                aria-expanded={ouvert}
              >
                <span
                  className={`tabulaire flex h-12 w-12 shrink-0 items-center justify-center rounded-md text-lg font-semibold ${
                    anomalie.nombre > 0
                      ? 'bg-amber-100 text-amber-900'
                      : 'bg-emerald-100 text-emerald-800'
                  }`}
                >
                  {anomalie.nombre}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{details.titre}</span>
                  {details.explication && (
                    <span className="mt-0.5 block text-sm text-encre-500">
                      {details.explication}
                    </span>
                  )}
                  {total > anomalie.nombre && (
                    <span className="mt-0.5 block text-xs text-encre-400">
                      {total - anomalie.nombre} assumée(s) sur {annee.annee?.libelle}
                    </span>
                  )}
                </span>
                {total > 0 && (
                  <span className="shrink-0 text-encre-400" aria-hidden>
                    {ouvert ? '▴' : '▾'}
                  </span>
                )}
              </button>

              {ouvert && total > 0 && (
                <ul className="divide-y divide-encre-100 border-t border-encre-200">
                  {anomalie.personnes.map((personne) => {
                    const assumee = personne.exception_id !== null;
                    return (
                      <li
                        key={personne.id}
                        className={`flex flex-wrap items-center gap-3 px-4 py-2.5 ${
                          assumee ? 'bg-encre-50' : ''
                        }`}
                      >
                        <Link
                          to={`/adherents/${personne.id}`}
                          className={`min-w-0 shrink-0 text-sm underline ${
                            assumee ? 'text-encre-400' : 'text-encre-800'
                          }`}
                        >
                          {personne.prenom} <span className="uppercase">{personne.nom}</span>
                        </Link>

                        {/* Les activites suivies : une date de naissance
                            manquante se demande au professeur qui a
                            l'eleve en cours, pas au hasard. */}
                        {personne.activites.map((activite) => (
                          <Badge key={activite}>{activite}</Badge>
                        ))}

                        {personne.exception_motif && (
                          <span className="truncate text-xs text-encre-500">
                            {personne.exception_motif}
                          </span>
                        )}

                        {gerable && (
                          <label className="ml-auto flex shrink-0 items-center gap-2 text-xs text-encre-500">
                            <input
                              type="checkbox"
                              checked={assumee}
                              onChange={() => {
                                // Cocher demande confirmation : on retire un
                                // controle. Decocher le remet — geste sans
                                // risque, qui n'a pas a etre ralenti.
                                if (assumee) void lever(personne);
                                else setAConfirmer({ type: anomalie.type, personne });
                              }}
                            />
                            Assumée
                          </label>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-xs text-encre-400">
        Un adhérent sans lien de parenté n’est pas une anomalie : sur les données reprises, la
        grande majorité des adhérents sont inscrits seuls.
      </p>

      {annee.annee && (
        <ConfirmationException
          demande={aConfirmer}
          anneeId={annee.anneeId}
          anneeLibelle={annee.annee.libelle}
          onFermer={() => setAConfirmer(null)}
          onEnregistre={() => {
            setAConfirmer(null);
            anomalies.recharger();
            compteur.rafraichir();
          }}
        />
      )}
    </>
  );
}

/**
 * Confirmation avant d'assumer une anomalie.
 *
 * Le motif est facultatif, mais c'est lui qui rend l'exception relisible
 * dans six mois : sans lui, on retrouve un controle leve sans savoir par
 * qui ni pourquoi.
 */
function ConfirmationException({
  demande,
  anneeId,
  anneeLibelle,
  onFermer,
  onEnregistre,
}: {
  demande: { type: string; personne: PersonneEnAnomalie } | null;
  anneeId: string;
  anneeLibelle: string;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  const cle = demande ? `${demande.type}|${demande.personne.id}` : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setMotif('');
    setErreur(null);
  }
  if (!demande && initialisePour !== null) setInitialisePour(null);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    if (!demande) return;
    setErreur(null);
    setEnCours(true);
    try {
      await api.post('/api/anomalies/exceptions', {
        anneeId,
        personneId: demande.personne.id,
        type: demande.type,
        motif: motif || null,
      });
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const details = demande ? LIBELLES[demande.type] : undefined;

  return (
    <Modale titre="Assumer cette anomalie" ouverte={demande !== null} onFermer={onFermer}>
      {demande && (
        <form onSubmit={soumettre} className="space-y-4">
          <p className="text-sm">
            <span className="font-medium">
              {demande.personne.prenom} <span className="uppercase">{demande.personne.nom}</span>
            </span>{' '}
            — {details?.titre.toLowerCase() ?? demande.type}
          </p>

          <Champ
            libelle="Motif"
            aide="Facultatif, mais c’est lui qui rendra l’exception relisible dans six mois."
          >
            <input
              className="champ"
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="Refuse de communiquer sa date de naissance…"
              autoFocus
            />
          </Champ>

          <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-500">
            L’anomalie disparaîtra du compte pour <strong>{anneeLibelle}</strong> seulement. L’année
            suivante, le cas reviendra à l’examen — la donnée, elle, reste inchangée.
          </p>

          {erreur && (
            <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {erreur}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" className="bouton-secondaire" onClick={onFermer}>
              Annuler
            </button>
            <button type="submit" className="bouton-principal" disabled={enCours}>
              Assumer
            </button>
          </div>
        </form>
      )}
    </Modale>
  );
}
